/* ==========================================================================
   right-drawer.js — PRODUCTION (rd-)
   - Reusable drawer, injected content from hidden templates
   - Open:  [data-rd-open data-rd-title="..." data-rd-source="#TemplateId"]
   - Close: [data-rd-close] | overlay click | ESC
   - Scroll lock: html.drawer-lock with refcount html[data-drawer-lock-count]
   - PRO behavior:
     * No lock counter drift (locks only once per open cycle)
     * Reopen while closing re-locks immediately (since close unlocks early)
     * TransitionEnd-based close finalize (with fallback)
========================================================================== */

class RightDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-rd-overlay]');
    this.panel = this.querySelector('[data-rd-panel]');
    this.titleEl = this.querySelector('[data-rd-title]');
    this.bodyEl = this.querySelector('[data-rd-body]');

    this._locked = false;
    this._closeTimer = null;

    this._onKeydown = this._onKeydown.bind(this);
    this._onClick = this._onClick.bind(this);

    document.addEventListener('click', this._onClick, true);

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Safety baseline
    this.classList.remove('rd-open', 'rd-closing');
    this.setAttribute('aria-hidden', 'true');
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onClick, true);
    document.removeEventListener('keydown', this._onKeydown);

    this._clearCloseTimer();

    // If the element is removed while open/closing, never leave the page locked.
    if (this._locked) this._unlockScroll(true);
  }

  /* -------------------------
     Public API
  ------------------------- */

  open(opener = null, opts = {}) {
    this._opener = opener || document.activeElement;

    const title = (opts.title || '').trim();
    const source = (opts.source || '').trim();

    // Optional: close other drawers (safe)
    document.querySelector('search-drawer')?.close?.();
    document.querySelector('cart-drawer')?.close?.();

    // If we were closing, cancel it immediately (and re-lock because close unlocks early)
    if (this.classList.contains('rd-closing')) {
      this._clearCloseTimer();
      this.classList.remove('rd-closing');
      this._lockScroll();
    }

    // Set content every time (supports “swap while open”)
    this._setTitle(title);
    this._setContentFromSource(source);

    // If already open, DO NOT re-lock / re-add listeners / reflow
    if (this.classList.contains('rd-open')) {
      this.setAttribute('aria-hidden', 'false');
      return;
    }

    // First-time open
    this._lockScroll();

    this.classList.add('rd-open');
    this.setAttribute('aria-hidden', 'false');

    document.addEventListener('keydown', this._onKeydown, { passive: false });

    // Focus panel then first focusable (like your SearchDrawer vibe)
    window.setTimeout(() => {
      this.panel?.focus?.();
      const first = this.querySelector(
        '[data-rd-body] a[href], [data-rd-body] button:not([disabled]), [data-rd-body] input:not([disabled]), [data-rd-body] select:not([disabled]), [data-rd-body] textarea:not([disabled]), [data-rd-body] [tabindex]:not([tabindex="-1"])'
      );
      first?.focus?.();
    }, 30);
  }

  close() {
    // If not open, ignore
    if (!this.classList.contains('rd-open')) return;

    // If a close is already scheduled, reset it cleanly
    this._clearCloseTimer();

    this.classList.remove('rd-open');
    this.classList.add('rd-closing');
    this.setAttribute('aria-hidden', 'true');

    document.removeEventListener('keydown', this._onKeydown, { passive: false });

    // ✅ Premium: unlock immediately (no dead-scroll lag)
    this._unlockScroll();

    const panel = this.panel;
    const closeDur = this._getMsVarFromHost('--rd-close-dur', 950);

    const finish = () => {
      // If reopened while closing, do nothing
      if (this.classList.contains('rd-open')) return;

      this.classList.remove('rd-closing');

      // Clear injected content after close (keeps DOM clean)
      this._clearContent();

      if (this._opener && typeof this._opener.focus === 'function') {
        try {
          this._opener.focus({ preventScroll: true });
        } catch (_) {
          this._opener.focus();
        }
      }
      this._opener = null;
    };

    // Prefer transitionend (premium, no “lag lock”)
    if (panel) {
      const onEnd = (e) => {
        if (e.target !== panel) return;
        if (e.propertyName && e.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onEnd);
        this._clearCloseTimer();
        finish();
      };

      panel.addEventListener('transitionend', onEnd);

      // Fallback (Safari/iOS sometimes drops transitionend)
      this._closeTimer = window.setTimeout(() => {
        panel.removeEventListener('transitionend', onEnd);
        this._closeTimer = null;
        finish();
      }, closeDur + 160);
    } else {
      // No panel? Just finish
      this._closeTimer = window.setTimeout(() => {
        this._closeTimer = null;
        finish();
      }, closeDur + 30);
    }
  }

  /* -------------------------
     Events
  ------------------------- */

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  }

  _onClick(e) {
    const opener = e.target.closest('[data-rd-open]');
    if (opener) {
      e.preventDefault();
      this.open(opener, {
        title: opener.getAttribute('data-rd-title') || '',
        source: opener.getAttribute('data-rd-source') || '',
      });
      return;
    }

    const closer = e.target.closest('[data-rd-close]');
    if (closer && this.contains(closer)) {
      e.preventDefault();
      this.close();
    }
  }

  /* -------------------------
     Content
  ------------------------- */

  _setTitle(title) {
    if (!this.titleEl) return;
    this.titleEl.textContent = title ? title : 'Info';
  }

  _setContentFromSource(selector) {
    if (!this.bodyEl) return;

    this.bodyEl.innerHTML = '';

    if (!selector) return;

    const src = document.querySelector(selector);
    if (!src) {
      this.bodyEl.innerHTML = '<p>Content not found.</p>';
      return;
    }

    this.bodyEl.innerHTML = src.innerHTML;

    // Strip scripts (safety)
    this.bodyEl.querySelectorAll('script').forEach((s) => s.remove());

    // Auto-wrap tables for horizontal scroll (premium)
    this.bodyEl.querySelectorAll('table').forEach((t) => {
      if (t.closest('.rd-table')) return;
      const wrap = document.createElement('div');
      wrap.className = 'rd-table';
      t.parentNode.insertBefore(wrap, t);
      wrap.appendChild(t);
    });
  }

  _clearContent() {
    if (this.bodyEl) this.bodyEl.innerHTML = '';
  }

  /* -------------------------
     Locking (refcount-safe)
  ------------------------- */

  _lockScroll() {
    if (this._locked) return;
    this._locked = true;

    const html = document.documentElement;
    const cur = Number(html.dataset.drawerLockCount || 0) + 1;
    html.dataset.drawerLockCount = String(cur);
    html.classList.add('drawer-lock');
  }

  _unlockScroll(force = false) {
    if (!this._locked && !force) return;
    this._locked = false;

    const html = document.documentElement;
    const cur = Number(html.dataset.drawerLockCount || 0);
    const next = Math.max(0, cur - 1);
    html.dataset.drawerLockCount = String(next);
    if (next === 0) html.classList.remove('drawer-lock');
  }

  /* -------------------------
     Helpers
  ------------------------- */

  _clearCloseTimer() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  _getMsVarFromHost(name, fallback) {
    // Reads CSS var from the host element (right-drawer.rd-drawer { --rd-close-dur: .95s; })
    const raw = getComputedStyle(this).getPropertyValue(name).trim();
    if (!raw) return fallback;

    if (raw.endsWith('ms')) return parseFloat(raw) || fallback;
    if (raw.endsWith('s')) return (parseFloat(raw) * 1000) || fallback;

    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  }
}

if (!customElements.get('right-drawer')) {
  customElements.define('right-drawer', RightDrawer);
}